<div class="code-information-container">
  <form [formGroup]="codeInfoForm" role="form" [attr.aria-label]="mode === 'view' && hcpcsCodeData?.code ? 'Code information form for ' + hcpcsCodeData.code : 'Code information form'">
    <div class="grid grid-cols-12 gap-8" [ngClass]="{'mb-8': searchCodeValue}">
      <!-- Code Information Section -->
      <div [ngClass]="searchCodeValue ? 'col-span-6' : 'col-span-12'">
        <h2 class="font-semibold section-heading" style="font-size: 1.25rem; margin-bottom: 2.0rem;">Code Information</h2>
        
        <!-- Search Code with Autocomplete -->
        <div class="field" *ngIf="mode !== 'view' && !isExpandedViewEdit">
          <label for="searchCode" class="field-required">Search for Code:</label>
          <div class="field-input-wrapper">
            <p-iconfield>
              <p-inputicon class="pi pi-search" (click)="searchCodeInput.inputEL?.nativeElement.focus()" />
              <p-autoComplete
                #searchCodeInput
                inputId="searchCode"
                formControlName="searchCode"
                [suggestions]="codeSuggestions"
                (completeMethod)="onSearchCodeQuery($event)"
                (onSelect)="onAutocompleteCodeSelected($event)"
                (keypress)="restrictToAlphanumeric($event)"
                [minLength]="1"
                placeholder="E0000"
                [dropdown]="false"
                [style]="{ width: '100%', display: 'block' }"
                [inputStyle]="{ width: '100%' }"
                [class.ng-invalid]="codeInfoForm.get('searchCode')?.invalid && codeInfoForm.get('searchCode')?.touched"
                aria-describedby="searchCode-error"
                aria-required="true"
                [appendTo]="'body'">
              </p-autoComplete>
              <p-inputicon 
                *ngIf="codeInfoForm.get('searchCode')?.value" 
                class="pi pi-times" 
                styleClass="cancel-icon"
                (click)="clearSearchCode()" />
            </p-iconfield>
          </div>
          <small 
            id="searchCode-error"
            *ngIf="codeInfoForm.get('searchCode')?.invalid && codeInfoForm.get('searchCode')?.dirty && codeInfoForm.get('searchCode')?.touched" 
            class="field-error basis-full mt-1 text-xs"
            role="alert">
            Code is required and must be alphanumeric
          </small>
        </div>

        <!-- Search Results Table -->
        <div class="search-results-wrapper" *ngIf="(selectedCodeResult || isCodeNotFound) && mode !== 'view' && !isExpandedViewEdit">
          <p-table [value]="selectedCodeResult ? [selectedCodeResult] : []">
            <ng-template pTemplate="header">
              <tr>
                <th>Code</th>
                <th>Short Description</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-result>
              <tr>
                <td>{{ result.code }}</td>
                <td>{{ result.shortDescription }}</td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr>
                <td colspan="2">No codes found.</td>
              </tr>
            </ng-template>
          </p-table>
        </div>

        <div class="card-grid small-gap" [ngClass]="{'mt-0': mode === 'view' || isExpandedViewEdit, 'mt-4': mode !== 'view' && !isExpandedViewEdit, 'edit-mode': mode !== 'view', 'view-mode-spacing': mode === 'view'}">
          <!-- Code - View Mode or Expanded Edit Mode -->
          <ng-container *ngIf="mode === 'view' || isExpandedViewEdit">
            <label>Code:</label>
            <div>{{ hcpcsCodeData?.code }}</div>
          </ng-container>

          <!-- Short Description -->
          <label for="shortDescription" [class.field-required]="mode !== 'view'">Short Description:</label>
          <div *ngIf="mode === 'view'">{{ hcpcsCodeData?.shortDescription }}</div>
          <div *ngIf="mode !== 'view'" class="flex flex-col" [ngClass]="searchCodeValue ? 'full-width-input' : 'medium-width-input'">
            <input 
              pInputText 
              id="shortDescription" 
              formControlName="shortDescription"
              class="w-full"
              maxlength="100"
              aria-required="true"
              aria-invalid="{{codeInfoForm.get('shortDescription')?.invalid && codeInfoForm.get('shortDescription')?.touched}}"
              [class.ng-invalid]="codeInfoForm.get('shortDescription')?.invalid && codeInfoForm.get('shortDescription')?.touched" />
            <small 
              *ngIf="codeInfoForm.get('shortDescription')?.invalid && codeInfoForm.get('shortDescription')?.touched" 
              class="text-red-600 text-xs mt-1"
              role="alert">
              Short description is required (max 100 characters)
            </small>
          </div>

          <!-- CMS Effective Start Date -->
          <ng-container *ngIf="searchCodeValue || mode === 'view'">
            <label for="cmsEffectiveStartDate" [class.field-required]="mode !== 'view' && !isExistingCode">CMS Effective Start Date:</label>
            <div *ngIf="mode === 'view'">{{ hcpcsCodeData?.cmsEffectiveStartDate | date:'MM/dd/yyyy' }}</div>
            <div *ngIf="mode !== 'view'" class="flex flex-col">
              <div *ngIf="isExistingCode" class="flex items-center text-sm min-h-9 min-w-[6.25rem]">
                {{ codeInfoForm.get('cmsEffectiveStartDate')?.value | date:'MM/dd/yyyy' }}
              </div>
              <p-datepicker 
                *ngIf="!isExistingCode"
                id="cmsEffectiveStartDate"
                formControlName="cmsEffectiveStartDate"
                dateFormat="mm/dd/yy"
                [showIcon]="true"
                placeholder="mm/dd/yyyy"
                [class.ng-invalid]="codeInfoForm.get('cmsEffectiveStartDate')?.invalid && codeInfoForm.get('cmsEffectiveStartDate')?.touched">
              </p-datepicker>
              <small *ngIf="codeInfoForm.get('cmsEffectiveStartDate')?.invalid && codeInfoForm.get('cmsEffectiveStartDate')?.touched" class="text-red-600 text-xs mt-1">
                CMS Effective Start Date is required
              </small>
            </div>
          </ng-container>

          <!-- CMS Effective End Date -->
          <ng-container *ngIf="searchCodeValue || mode === 'view'">
            <label for="cmsEffectiveEndDate">CMS Effective End Date:</label>
            <div *ngIf="mode === 'view'">{{ hcpcsCodeData?.cmsEffectiveEndDate ? (hcpcsCodeData?.cmsEffectiveEndDate | date:'MM/dd/yyyy') : '-' }}</div>
            <div *ngIf="mode !== 'view'" class="flex flex-col">
              <div *ngIf="isExistingCode" class="flex items-center text-sm min-h-9 min-w-[6.25rem]">
                {{ codeInfoForm.get('cmsEffectiveEndDate')?.value ? (codeInfoForm.get('cmsEffectiveEndDate')?.value | date:'MM/dd/yyyy') : '-' }}
              </div>
              <p-datepicker
                *ngIf="!isExistingCode"
                id="cmsEffectiveEndDate"
                formControlName="cmsEffectiveEndDate"
                dateFormat="mm/dd/yy"
                [showIcon]="true"
                placeholder="mm/dd/yyyy"
                [minDate]="codeInfoForm.get('cmsEffectiveStartDate')?.value">
              </p-datepicker>
            </div>
          </ng-container>

          <!-- DMEPOS Category -->
          <ng-container *ngIf="searchCodeValue || mode === 'view'">
            <label for="dmeposCategory" class="dmepos-category-label" [class.field-required]="mode !== 'view' && !codeInfoForm.get('dmeposCategory')?.disabled">DMEPOS Category:</label>
            <div *ngIf="mode === 'view'">{{ hcpcsCodeData?.dmeposCategory || '-' }}</div>
            <div *ngIf="mode !== 'view'" class="flex flex-col dmepos-category-field">
              <p-select
                id="dmeposCategory"
                formControlName="dmeposCategory"
                [options]="dmeposCategoryOptions"
                placeholder="Select"
                optionLabel="label"
                optionValue="value"
                [showClear]="true"
                [class.ng-invalid]="codeInfoForm.get('dmeposCategory')?.invalid && codeInfoForm.get('dmeposCategory')?.touched">
              </p-select>
              <small *ngIf="codeInfoForm.get('dmeposCategory')?.invalid && codeInfoForm.get('dmeposCategory')?.touched" class="text-red-600 text-xs mt-1">
                DMEPOS Category is required
              </small>
              <p-message 
                *ngIf="!isHcpcsCodeExists && !codeInfoForm.get('dmeposCategory')?.value && codeInfoForm.get('planType')?.value" 
                severity="warn" 
                [motionOptions]="{ appear: false }" 
                icon="pi pi-exclamation-triangle"
                class="mt-1 dmepos-warning">
                <span class="text-xs">Your Code is being added. Please, come back in 24 hours to set the DMEPOS Category.</span>
              </p-message>
            </div>
          </ng-container>
        </div>
      </div>

      <!-- Program Section -->
      <div [ngClass]="searchCodeValue ? 'col-span-6' : 'col-span-12'" *ngIf="searchCodeValue || mode === 'view'">
        <h2 class="font-semibold section-heading" style="font-size: 1.25rem; margin-bottom: 2.0rem;">Program</h2>
        
        <div class="card-grid small-gap" [ngClass]="{'edit-mode': mode !== 'view', 'view-mode-spacing': mode === 'view'}">
          <!-- Program -->
          <label for="program" [class.field-required]="mode !== 'view'">Program:</label>
          <div *ngIf="mode === 'view'">{{ hcpcsCodeData?.productShortName || '-' }}</div>
          <div *ngIf="mode !== 'view'" class="flex flex-col">
            <p-select
              id="program"
              formControlName="program"
              [options]="programOptions"
              placeholder="Select"
              optionLabel="label"
              optionValue="value"
              [showClear]="true"
              [attr.aria-required]="true"
              [attr.aria-invalid]="codeInfoForm.get('program')?.invalid && codeInfoForm.get('program')?.touched"
              [attr.aria-describedby]="'program-error'"
              [class.ng-invalid]="codeInfoForm.get('program')?.invalid && codeInfoForm.get('program')?.touched">
            </p-select>
            <small 
              id="program-error"
              *ngIf="codeInfoForm.get('program')?.invalid && codeInfoForm.get('program')?.touched" 
              class="text-red-600 text-xs mt-1"
              role="alert">
              Program is required
            </small>
          </div>

          <!-- Health Plan -->
          <label for="healthPlan">Health Plan:</label>
          <div *ngIf="mode === 'view'">{{ hcpcsCodeData?.tenant || '-' }}</div>
          <div *ngIf="mode !== 'view'" class="flex flex-col">
            <p-select
              id="healthPlan"
              formControlName="healthPlan"
              [options]="healthPlanOptions"
              placeholder="Select"
              optionLabel="label"
              optionValue="value"
              [showClear]="true">
            </p-select>
          </div>

          <!-- Plan Type -->
          <label for="planType">Plan Type:</label>
          <div *ngIf="mode === 'view'">{{ hcpcsCodeData?.plan || '-' }}</div>
          <div *ngIf="mode !== 'view'" class="flex flex-col">
            <p-select
              id="planType"
              formControlName="planType"
              [options]="planTypeOptions"
              placeholder="Select"
              optionLabel="label"
              optionValue="value"
              [showClear]="true">
            </p-select>
          </div>

          <!-- Plan Effective Start Date -->
          <ng-container *ngIf="searchCodeValue || mode === 'view'">
            <label for="planEffectiveStartDate" [class.field-required]="mode !== 'view'">Plan Effective Start Date:</label>
            <div *ngIf="mode === 'view'">{{ hcpcsCodeData?.planEffectiveStartDate | date:'MM/dd/yyyy' }}</div>
            <div *ngIf="mode !== 'view'" class="flex items-center gap-2">
            <!-- Show static text when locked -->
            <div *ngIf="isPlanStartDateLocked" class="flex items-center text-sm min-h-9 min-w-[6.25rem]">
              {{ codeInfoForm.get('planEffectiveStartDate')?.value | date:'MM/dd/yyyy' }}
            </div>
            <!-- Show date picker when unlocked -->
            <p-datepicker 
              [hidden]="isPlanStartDateLocked"
              id="planEffectiveStartDate"
              formControlName="planEffectiveStartDate"
              dateFormat="mm/dd/yy"
              [showIcon]="true"
              placeholder="mm/dd/yyyy"
              [maxDate]="codeInfoForm.get('planEffectiveEndDate')?.value"
              [class.ng-invalid]="codeInfoForm.get('planEffectiveStartDate')?.hasError('dateRangeInvalid')">
            </p-datepicker>
            <button 
              *ngIf="isPlanStartDateLocked"
              type="button"
              pButton
              icon="pi pi-lock"
              class="p-button-text p-button-sm lock-button"
              (click)="togglePlanStartDateLock()"
              aria-label="Unlock plan effective start date"
              [attr.aria-pressed]="isPlanStartDateLocked"
              pTooltip="Click to unlock Plan Effective Dates if they differ from CMS Effective Dates"
              tooltipPosition="top">
            </button>
            </div>
          </ng-container>
          <small *ngIf="codeInfoForm.get('planEffectiveStartDate')?.hasError('dateRangeInvalid')" class="field-error block" style="grid-column: 1 / -1;">
            Start date cannot be greater than end date
          </small>

          <!-- Plan Effective End Date -->
          <ng-container *ngIf="searchCodeValue || mode === 'view'">
            <label for="planEffectiveEndDate">Plan Effective End Date:</label>
            <div *ngIf="mode === 'view'">{{ hcpcsCodeData?.planEffectiveEndDate ? (hcpcsCodeData?.planEffectiveEndDate | date:'MM/dd/yyyy') : '-' }}</div>
            <div *ngIf="mode !== 'view'" class="flex items-center gap-2">
              <div *ngIf="isPlanEndDateLocked" class="min-w-[6.25rem]">
                {{ codeInfoForm.get('planEffectiveEndDate')?.value ? (codeInfoForm.get('planEffectiveEndDate')?.value | date:'MM/dd/yyyy') : '-' }}
              </div>
              <p-datepicker 
                [hidden]="isPlanEndDateLocked"
                id="planEffectiveEndDate"
                formControlName="planEffectiveEndDate"
                dateFormat="mm/dd/yy"
                [showIcon]="true"
                placeholder="mm/dd/yyyy"
                [minDate]="codeInfoForm.get('planEffectiveStartDate')?.value"
                [class.ng-invalid]="codeInfoForm.get('planEffectiveEndDate')?.hasError('dateRangeInvalid')">
              </p-datepicker>
              <button 
                *ngIf="isPlanEndDateLocked"
                type="button"
                pButton
                icon="pi pi-lock"
                class="p-button-text p-button-sm lock-button"
                (click)="togglePlanEndDateLock()"
                aria-label="Unlock plan effective end date"
                [attr.aria-pressed]="isPlanEndDateLocked"
                pTooltip="Click to unlock Plan Effective Dates if they differ from CMS Effective Dates"
                tooltipPosition="top">
              </button>
            </div>
          </ng-container>
        </div>
      </div>
    </div>
  </form>
</div>
